import React, { useState } from 'react'
import { Alert, Button, useToast } from '@apexinternal/ui-components'
import {
  Box,
  Link,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react'
import { updateAddress } from '../../../../actions'
import { Addresses } from 'types/CommonTypes'
import { StatesOptions } from '../../../../constants/states'
import { sentenceCase } from '../../../../utils'

export interface ConfirmModalContentProps {
  accountNumber: string
  content: Addresses
  homeIsSame: boolean
  loadEditModalContent: () => void
  onClose: () => void
}

export const SUCCESS_DESCRIPTION =
  'The address has been updated. Please allow up to 24 hours for this change to take effect.'

const ConfirmModalContent = ({
  accountNumber,
  content,
  homeIsSame,
  loadEditModalContent,
  onClose,
}: ConfirmModalContentProps) => {
  const [incorrectMailingError, setIncorrectMailingError] = useState(false)
  const [incorrectHomeError, setIncorrectHomeError] = useState(false)

  const handleError = (error: string) => {
    const splitError = error.split(' ')
    if (splitError[splitError.length - 1] === 'homeAddress') {
      setIncorrectHomeError(true)
    } else if (splitError[splitError.length - 1] === 'mailingAddress') {
      setIncorrectMailingError(true)
    } else {
      return toast({
        title: 'Error!',
        description: error,
        status: 'error',
        isClosable: true,
        duration: null,
      })
    }
  }

  const toast = useToast()
  const handleSubmit = (): void => {
    updateAddress(
      accountNumber,
      content,
      (error: string) => {
        handleError(error)
      },
      () => {
        toast({
          title: 'Success',
          description: SUCCESS_DESCRIPTION,
          status: 'success',
          isClosable: true,
        })
        onClose()
      }
    )
  }

  const mailingState =
    StatesOptions[content.mailingAddress.state as keyof typeof StatesOptions]
  const homeState = content.homeAddress
    ? StatesOptions[content.homeAddress.state as keyof typeof StatesOptions]
    : null

  return (
    <ModalContent>
      <ModalHeader>Confirm Address Changes</ModalHeader>
      {incorrectMailingError && (
        <Alert
          pr="20%"
          isClosable={true}
          dismiss={() => setIncorrectMailingError(false)}
          status="error"
          title="Notice!"
          description={`The combination of ${sentenceCase(
            content.mailingAddress.city
          )}, ${content.mailingAddress.state}, and ${
            content.mailingAddress.postalCode
          } is not valid for the mailing address.`}
        />
      )}
      {incorrectHomeError && content.homeAddress && (
        <Alert
          pr="20%"
          isClosable={true}
          dismiss={() => setIncorrectHomeError(false)}
          status="error"
          title="Notice!"
          description={`The combination of ${sentenceCase(
            content.homeAddress.city
          )}, ${content.homeAddress.state}, and ${
            content.homeAddress.postalCode
          } is not valid for the home address.`}
        />
      )}

      <ModalCloseButton onClick={onClose} />
      <ModalBody>
        <Text
          as="h4"
          textStyle="h4"
          fontWeight="semibold"
          data-testid="mailing-address"
        >
          Mailing Address
        </Text>
        <SimpleGrid columns={2}>
          <Box>
            <Text fontWeight="semibold" pt={5}>
              Street Address *
            </Text>
            <Text pt={5}>{content.mailingAddress.streetAddress[0]}</Text>
          </Box>
          {content.mailingAddress.streetAddress[1] && (
            <Box>
              <Text pt={5}>Street Address 2</Text>
              <Text pt={5}>{content.mailingAddress.streetAddress[1]}</Text>
            </Box>
          )}
          <Box>
            <Text fontWeight="semibold" pt={5}>
              City *
            </Text>
            <Text pt={5}>{content.mailingAddress.city}</Text>
          </Box>
          <Box>
            <Text fontWeight="semibold" pt={5}>
              State *
            </Text>
            <Text pt={5}>
              {mailingState} ({content.mailingAddress.state})
            </Text>
          </Box>
          <Box>
            <Text fontWeight="semibold" pt={5}>
              ZIP Code *
            </Text>
            <Text pt={5} pb={5}>
              {content.mailingAddress.postalCode}
            </Text>
          </Box>
        </SimpleGrid>
        {homeIsSame && (
          <>
            <Text pt={5} pb={5}>
              Home address is the same as the mailing address
            </Text>
            <Alert
              status="info"
              title="Notice!"
              description="It may take up to 24 hours to reflect your changes. A letter will be mailed to each address to confirm this update."
            />
          </>
        )}
        {content.homeAddress && (
          <>
            <Text
              as="h4"
              textStyle="h4"
              fontWeight="semibold"
              data-testid="home-address"
            >
              Home Address (optional)
            </Text>
            <SimpleGrid columns={2} spacing={2}>
              <Box>
                <Text fontWeight="semibold" pt={5}>
                  Street Address
                </Text>
                <Text pt={5}>{content.homeAddress.streetAddress[0]}</Text>
              </Box>
              {content.homeAddress.streetAddress[1] && (
                <Box>
                  <Text pt={5}>Street Address 2</Text>
                  <Text pt={5}>{content.homeAddress.streetAddress[1]}</Text>
                </Box>
              )}
              <Box>
                <Text fontWeight="semibold" pt={5}>
                  City
                </Text>
                <Text pt={5}>{content.homeAddress.city}</Text>
              </Box>
              <Box>
                <Text fontWeight="semibold" pt={5}>
                  State
                </Text>
                <Text pt={5}>
                  {homeState} ({content.homeAddress.state})
                </Text>
              </Box>
              <Box>
                <Text fontWeight="semibold" pt={5}>
                  ZIP Code
                </Text>
                <Text pt={5} pb={5}>
                  {content.homeAddress.postalCode}
                </Text>
              </Box>
            </SimpleGrid>
            <Alert
              status="info"
              title="Notice!"
              description="It may take up to 24 hours to reflect your changes. A letter will be mailed to each address to confirm this update."
            />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Link
          mr={52}
          fontWeight="semibold"
          color="apex.link"
          onClick={loadEditModalContent}
        >
          &#60; Back
        </Link>
        <Stack direction="row" spacing={2}>
          <Button variant="white" onClick={onClose} tabIndex={1}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Submit Update
          </Button>
        </Stack>
      </ModalFooter>
    </ModalContent>
  )
}

export default ConfirmModalContent
